import { API } from "aws-amplify";
import { API_ENDPOINT_NAME } from "../consts/constants";
import { loggingInfo, loggingWarn } from "../utils/LoggingUtils";

type GetLicensesRequest = {
    user_id?: string;
    group_id?: number;
    is_active?: boolean;
};

type GetLicenseusersRequest = {
    package_ids?: number[];
    insert_date_start?: string;
    insert_date_end?: string;
    expire_date_start?: string;
    expire_date_end?: string;
    only_free_trial?: boolean;
    only_product?: boolean;
    only_permanent?: boolean;
    only_subscription?: boolean;
    exclude_deepforest?: boolean;
};

type PutLicenseRequest = {
    activation_date?: string;
    expire_date?: string;
    support_start_date?: string;
    support_expiration_date?: string;
    last_support_expiration_date?: string;
};

/**
 * ライセンス情報一括取得処理
 * @param idToken IDトークン
 * @param requestInfo リクエストパラメータ
 * @returns ライセンス情報一括取得APIレスポンス
 */
export async function getLicenses(idToken: string, requestInfo: GetLicensesRequest) {
    const path = "/licenses";
    const body: { [name: string]: any } = {};
    if (requestInfo.user_id) {
        body["user_id"] = requestInfo.user_id;
    }
    if (requestInfo.group_id) {
        body["group_id"] = requestInfo.group_id;
    }
    if (requestInfo.is_active) {
        body["is_active"] = requestInfo.is_active;
    }
    const request = {
        body: body,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: idToken,
        },
    };
    try {
        const response = await API.post(API_ENDPOINT_NAME, path, request);
        loggingInfo("[GetLicenses] Succeed:", response);
        return response;
    } catch (error: any) {
        loggingWarn("[GetLicenses] Failed:", error);
        throw error;
    }
}

/**
 * ライセンス紐づけ処理
 * @param idToken IDトークン
 * @param  リクエストパラメータ
 * @returns ライセンス紐づけ解除APIレスポンス
 */
export async function linkLicense(idToken: string, licenseKey: string, userId: string) {
    const path = "/license/linking";
    const body = {
        license_key: licenseKey,
        user_id: userId,
    };
    const request = {
        body: body,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: idToken,
        },
    };
    try {
        const response = await API.post(API_ENDPOINT_NAME, path, request);
        loggingInfo("[LinkLicense] Succeed:", response);
        return response;
    } catch (error: any) {
        loggingWarn("[LinkLicense] Failed:", error);
        throw error;
    }
}

/**
 * ライセンス紐づけ解除処理
 * @param idToken IDトークン
 * @param  リクエストパラメータ
 * @returns ライセンス紐づけ解除APIレスポンス
 */
export async function unlinkLicense(idToken: string, licenseKey: string, userId: string) {
    const path = "/license/unlinking";
    const body = {
        license_key: licenseKey,
        user_id: userId,
    };
    const request = {
        body: body,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: idToken,
        },
    };
    try {
        const response = await API.post(API_ENDPOINT_NAME, path, request);
        loggingInfo("[UnlinkLicense] Succeed:", response);
        return response;
    } catch (error: any) {
        loggingWarn("[UnlinkLicense] Failed:", error);
        throw error;
    }
}

/**
 * ライセンス情報更新
 * @param idToken IDトークン
 * @param  リクエストパラメータ
 * @returns ライセンス情報更新APIレスポンス
 */
export async function putLicense(idToken: string, license_id: number, requestInfo: PutLicenseRequest) {
    const path = `/license/${license_id}`;
    const body: { [name: string]: any } = {};
    if (requestInfo.activation_date) {
        body["activation_date"] = requestInfo.activation_date;
    }
    if (requestInfo.expire_date) {
        body["expire_date"] = requestInfo.expire_date;
    }
    if (requestInfo.support_start_date) {
        body["support_start_date"] = requestInfo.support_start_date;
    }
    if (requestInfo.support_expiration_date) {
        body["support_expiration_date"] = requestInfo.support_expiration_date;
    }
    if (requestInfo.last_support_expiration_date) {
        body["last_support_expiration_date"] = requestInfo.last_support_expiration_date;
    }
    const request = {
        body: body,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: idToken,
        },
    };
    try {
        const response = await API.put(API_ENDPOINT_NAME, path, request);
        loggingInfo("[PutLicense] Succeed:", response);
        return response;
    } catch (error: any) {
        loggingWarn("[PutLicense] Failed:", error);
        throw error;
    }
}

/**
 * ライセンスユーザー情報一括取得
 * @param idToken IDトークン
 * @param requestInfo リクエストパラメータ
 * @returns ライセンスユーザー情報一括取得APIレスポンス
 */
export async function getLicenseusers(idToken: string, requestInfo: GetLicenseusersRequest) {
    const path = "/licenseusers";
    const body: { [name: string]: any } = {};
    if (requestInfo.package_ids) {
        body["package_ids"] = requestInfo.package_ids;
    }
    if (requestInfo.insert_date_start) {
        body["insert_date_start"] = requestInfo.insert_date_start;
    }
    if (requestInfo.insert_date_end) {
        body["insert_date_end"] = requestInfo.insert_date_end;
    }
    if (requestInfo.expire_date_start) {
        body["expire_date_start"] = requestInfo.expire_date_start;
    }
    if (requestInfo.expire_date_end) {
        body["expire_date_end"] = requestInfo.expire_date_end;
    }
    if (requestInfo.only_free_trial) {
        body["only_free_trial"] = requestInfo.only_free_trial;
    }
    if (requestInfo.only_product) {
        body["only_product"] = requestInfo.only_product;
    }
    if (requestInfo.only_permanent) {
        body["only_permanent"] = requestInfo.only_permanent;
    }
    if (requestInfo.only_subscription) {
        body["only_subscription"] = requestInfo.only_subscription;
    }
    if (requestInfo.exclude_deepforest != null && !requestInfo.exclude_deepforest) {
        body["exclude_deepforest"] = requestInfo.exclude_deepforest;
    }

    const request = {
        body: body,
        headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: idToken,
        },
    };
    try {
        const response = await API.post(API_ENDPOINT_NAME, path, request);
        loggingInfo("[GetLicenseusers] Succeed:", response);
        return response;
    } catch (error: any) {
        loggingWarn("[GetLicenseusers] Failed:", error);
        throw error;
    }
}
